import { Inject, Injectable } from '@angular/core';
import { LogInstanceToken, LogInterface } from '@evorait/pace-logging';
import { Observable, of } from 'rxjs';
import { catchError, map, take, tap } from 'rxjs/operators';
import { ConfigConstants } from '../../config/config-constants';
import { InitializableInterface } from '../../interfaces/initializable.interface';
import { ArrayModel } from '../../models/complex/array.model';
import { MaterialBillingIndicatorModel } from '../../models/complex/material-billing-indicator.model';
import { MaterialStockModel } from '../../models/complex/material-stock.model';
import { BootStockLocalMaterialModel } from '../../models/low-level/boot-stock-local-material.model';
import { MATERIAL_STATUS_CODE } from '../../models/low-level/material-status.model';
import { MaterialModel } from '../../models/low-level/material.model';
import { CSE_STOCK } from '../../models/low-level/material.enum';
import { NotificationModel } from '../../models/low-level/notification.model';
import { EventService } from '../event/event.service';
import { OdataMaterialService } from '../material/odata-material.service';
import { UserMasterInstanceToken, UserMasterInterface } from '../../interfaces/user-master.interface';
import { LocalStorageCseStockService } from './local-storage-cse-stock.service';
import { isDefined } from '../../utilities/is-defined';
import { MaterialService } from '../material/material.service';
import { PartModel } from '../../models/complex/part.model';
import { MATERIAL_SOURCE_CODE } from '../../models/low-level/material-source.model';

@Injectable({
    providedIn: 'root',
})
export class CseStockService implements InitializableInterface {
    public isInitialized = false;
    private bootStockMaterialModels: MaterialStockModel;
    public constructor(
        @Inject(LogInstanceToken) private readonly log: LogInterface,
        private readonly odataMaterialService: OdataMaterialService,
        @Inject(UserMasterInstanceToken) private readonly userMasterService: UserMasterInterface,
        private readonly eventService: EventService,
        private readonly localStorageCseStockService: LocalStorageCseStockService,
        private readonly materialService: MaterialService,
    ) {
        // Register for event telling that the Mat stock set sync has been completed
        this.eventService.bootStockSetSyncCompleted$.subscribe(() => {
            this.localStorageCseStockService.deleteBootStockMaterials();
        });
    }

    public init(): Observable<MaterialStockModel> {
        this.log.debug('CseStockService: init -> boot stock service');
        return this.updateBootStock().pipe(
            tap(() => {
                this.isInitialized = true;
            }),
        );
    }

    public updateBootStock(): Observable<MaterialStockModel> {
        const userModel = this.userMasterService.getSelectedUserModel();
        return this.odataMaterialService.getMaterialBootStock(userModel).pipe(
            tap((models) => {
                this.bootStockMaterialModels = models;
            }),
        );
    }

    public updateMatBillingIndicator(
        materialBootStock: MaterialModel[],
        payload: MaterialBillingIndicatorModel,
    ): Observable<MaterialModel[]> {
        return this.odataMaterialService.getMaterialBillingIndicators(payload).pipe(
            take(1),
            map((matBillingIndicatorModel) => {
                materialBootStock.forEach((material) => {
                    const billingIndicators = matBillingIndicatorModel.materialBillingChangeable.value.find(
                        (billableChangeableModel) =>
                            billableChangeableModel.material.value === material.materialIdentifier.materialNumber.value,
                    );
                    if (isDefined(billingIndicators)) {
                        material.billableCode.selectByPredicate(
                            (entry) => entry.model.value === billingIndicators.billable.value,
                        );
                        material.billableCode.options = {
                            ...material.billableCode.options,
                            disabled:
                                billingIndicators.changeable.value ===
                                    ConfigConstants.MATERIAL_BILLABLE_CHANGEABLE.No ||
                                billingIndicators.billable.value.length === 0,
                            required:
                                billingIndicators.changeable.value !== ConfigConstants.MATERIAL_BILLABLE_CHANGEABLE.No,
                        };
                    } else {
                        this.log.warn(
                            `CseStockService: updateMatBillingIndicator: billing indicators not received for ${material.materialIdentifier.materialNumber.value}`,
                        );
                    }
                });
                return materialBootStock;
            }),
            catchError((error) => {
                this.log.error(
                    `CseStockService: updateMatBillingIndicator: get billing indicators for notification ${
                        payload.notificationNo.value
                    } error ${JSON.stringify(error)}`,
                );
                // select Auto as default, and should be disabled
                materialBootStock.forEach((material) => {
                    material.billableCode.selectByPredicate(
                        (entry) => entry.model.value === ConfigConstants.BILLABLE_CODE.AUTO,
                    );
                    material.billableCode.options = {
                        ...material.billableCode.options,
                        disabled: true,
                        required: true,
                    };
                });
                return of(materialBootStock);
            }),
        );
    }

    /**
     * This function will create the material models based on the input cse stock response
     * and the offline support provided by the cse stock(for now only boot stock offline support is possible)
     *
     * @param matStockSet
     * @param notificationModel
     * @param consumptionList
     * @param cseStock
     */
    public async createCseStockMaterialModelsAsync(
        matStockSet: MaterialStockModel,
        notificationModel: NotificationModel | undefined,
        consumptionList: ArrayModel<MaterialModel>,
        cseStock: CSE_STOCK,
    ): Promise<MaterialModel[]> {
        if (!this.isInitialized) {
            throw new Error(`Trying to call CseStockService before initialization!`);
        }

        const availableStockPartSet = matStockSet.partSet;
        const availableStockMaterialsLongInfo = matStockSet.longTextInfo;
        // if there is any boot stock data in local storage
        // then replace the part model quantity with the locally stored boot stock material quantity
        if (cseStock === CSE_STOCK.BOOT_STOCK) {
            const localBootStockMaterials = await this.getLocalBootStockMaterialModelsAsync();
            if (localBootStockMaterials.length > 0) {
                availableStockPartSet.forEach((stockPartModel) => {
                    const localBootStockMaterial = localBootStockMaterials.find((localBootStock) => {
                        return (
                            localBootStock.materialNumber === stockPartModel.materialIdentifier.materialNumber.value &&
                            localBootStock.storageLocation === stockPartModel.storageLocation.value
                        );
                    });
                    if (localBootStockMaterial) {
                        stockPartModel.quantity.value = localBootStockMaterial.quantity;
                    }
                });
            }
        }

        const profInfo = availableStockMaterialsLongInfo.find((info) => {
            const qmNum = notificationModel ? notificationModel.identifier.getQmnum() : '';
            return info.textId === 'PROF' && info.notificationNo === qmNum;
        });
        const stofInfo = availableStockMaterialsLongInfo.find((info) => {
            const qmNum = notificationModel ? notificationModel.identifier.getQmnum() : '';
            return info.textId === 'STOF' && info.notificationNo === qmNum;
        });
        const profText = profInfo ? profInfo.textLine : '';
        const stofText = stofInfo ? stofInfo.textLine : '';
        const locallyStoredStockMaterial = this.materialService.getPickedMatStockFromExistingNotification();
        // BootStock is triggered from two screens: Side Menu and Confirmation Screen.
        // CRM Screen:We receive `consumptionList` as a parameter, which already contains all picked BootStock materials.
        // Side Menu: Since no consumption information is available from the Side Menu, we rely on all locally stored picked materials.
        const materials = consumptionList.value.length > 0 ? consumptionList.value : locallyStoredStockMaterial;
        // Create Material Model for those boot stock material whose remaining quantity is greater than 0
        const matStockSetArray: MaterialModel[] = [];

        availableStockPartSet.forEach((stockPartModel) => {
            const pickedQuantity = this.getBootStockPickedQuantity(materials, stockPartModel);
            const quantity = (stockPartModel.quantity.value ?? 0) - pickedQuantity;
            if (quantity > 0) {
                stockPartModel.dangerousGoodsText.value = profText;
                stockPartModel.hazardousMaterialText.value = stofText;
                // each row in cse stock UI is of type materialModel.
                const cseMaterialStock = new MaterialModel(stockPartModel, notificationModel);
                // Initially material in cse stock should have quantity 0  by default
                // User has to increase the quantity he needs to pickup
                cseMaterialStock.quantity.value = 0;
                cseMaterialStock.quantity.options = { ...cseMaterialStock.quantity.options, minValue: 0 };
                cseMaterialStock.deliveredQuantity = quantity;
                // By default, the Material status will be picked up
                // as Once it is added in the consumption list it should not be needed to pickup again
                cseMaterialStock.materialStatus.statusCode = MATERIAL_STATUS_CODE.PICKED;
                if (cseStock === CSE_STOCK.BOOT_STOCK) {
                    cseMaterialStock.materialSource.sourceCode = MATERIAL_SOURCE_CODE.BOOTSTOCK;
                } else if (cseStock === CSE_STOCK.OTHER_STOCK) {
                    cseMaterialStock.materialSource.sourceCode = MATERIAL_SOURCE_CODE.OTHERSTOCK;
                }
                matStockSetArray.push(cseMaterialStock);
            }
        });
        return matStockSetArray;
    }

    private getBootStockPickedQuantity(existingMaterials: MaterialModel[], stockPartModel: PartModel): number {
        return existingMaterials.reduce((acc, consumedMaterial) => {
            const isSameStorageLocation =
                consumedMaterial.storageLocation.value === stockPartModel.storageLocation.value;

            const isSameMaterial =
                consumedMaterial.materialIdentifier.materialNumber.value ===
                stockPartModel.materialIdentifier.materialNumber.value;

            return isSameStorageLocation && isSameMaterial ? acc + (consumedMaterial.quantity.value ?? 0) : acc;
        }, 0);
    }

    /**
     * This function will return all the stored boot stock material from local storage
     */
    public async getLocalBootStockMaterialModelsAsync(): Promise<BootStockLocalMaterialModel[]> {
        if (!this.isInitialized) {
            throw new Error(`Trying to call CseStockService before initialization!`);
        }

        return this.localStorageCseStockService.getBootStockLocalMaterialModelsAsync();
    }

    /**
     * This function will save input materials into local storage
     *
     * @param materials
     */
    public setLocalBootStockMaterialModels(materials: MaterialModel[]): void {
        if (!this.isInitialized) {
            throw new Error(`Trying to call CseStockService before initialization!`);
        }

        // 1.Load all locally stored materials.
        this.localStorageCseStockService
            .getBootStockLocalMaterialModelsAsync()
            .then((localBootStockMaterials) => {
                // const localBootStockMaterials = this.localStorageCseStockService.getBootStockLocalMaterialModels();

                // 2.For each material, find the matching local material.
                materials.forEach((material) => {
                    const localBootStockMaterial = localBootStockMaterials.find((localBootStock) => {
                        return (
                            localBootStock.materialNumber === material.materialIdentifier.materialNumber.value &&
                            localBootStock.storageLocation === material.storageLocation.value
                        );
                    });

                    // 3.If there is matched local material, update its quantity.
                    if (localBootStockMaterial) {
                        localBootStockMaterial.quantity =
                            localBootStockMaterial.quantity - (material.quantity.value ?? 0);
                    } else {
                        const matchingBootStockMaterial = this.bootStockMaterialModels.partSet.find(
                            (offlineBootStockMaterial) => {
                                return (
                                    offlineBootStockMaterial.materialIdentifier.materialNumber.value ===
                                        material.materialIdentifier.materialNumber.value &&
                                    offlineBootStockMaterial.storageLocation.value === material.storageLocation.value
                                );
                            },
                        );

                        if (!matchingBootStockMaterial) {
                            throw new Error('Could not find matching boot stock material to update!');
                        }

                        // 4.If no local material found, push a new local material into the local material array,update its quantity
                        localBootStockMaterials.push(
                            new BootStockLocalMaterialModel(
                                material.materialIdentifier.materialNumber.value,
                                material.storageLocation.value,
                                (matchingBootStockMaterial.quantity.value ?? 0) - (material.quantity.value ?? 0),
                            ),
                        );
                    }
                });

                // 5.finally save the updated materials into local storage
                return this.localStorageCseStockService.saveBootStockLocalMaterialModels(localBootStockMaterials);
            })
            .catch((error) => this.log.error(this.constructor.name, 'failed to store boot stock materials', error));
    }
}
